/**
 * Which IPFS gateways the landing page may show to a reader.
 *
 * Gibs runs some IPFS endpoints: the release address, the per-release gateway,
 * and the Pinata custom domain. The page does not publish them.
 * It opens a release on a neutral public gateway that Gibs does not run. One
 * list of forbidden host patterns lives here, so the page and every guard test
 * read the same rule.
 */
import { buildGatewayLink } from './format'
import {
  DEFAULT_RELEASE_GATEWAYS,
  type GatewayBase,
  REFUSING_GATEWAYS,
  type VersionManifest,
} from '../../../scripts/ipfs-release/schema'

/**
 * Host patterns for IPFS endpoints that Gibs hosts. Each pattern matches a bare
 * host name. The page refuses to pick a gateway that matches one.
 */
export const GIBS_HOSTED_IPFS_HOST_PATTERNS: readonly RegExp[] = Object.freeze([
  /^ipfs\.gibs\.finance$/i,
  /^[a-z0-9]+-ipfs\.gibs\.finance$/i,
  /^pinata\.gibs\.finance$/i,
  /^[a-z0-9]+\.ipfs\.gib\.show$/i,
  /^(?:[a-z0-9-]+\.)*ipfs\.valve\.city$/i,
])

/**
 * A scan pattern that finds any Gibs-hosted IPFS host inside text or markup.
 * Guard tests run it over the built pages. It is the same rule as
 * {@link GIBS_HOSTED_IPFS_HOST_PATTERNS}, written for free text.
 */
export const GIBS_HOSTED_IPFS_TEXT_PATTERN =
  /(?:[a-z0-9]+-)?ipfs\.gibs\.finance|pinata\.gibs\.finance|[a-z0-9]+\.ipfs\.gib\.show|gib\.show\/ipfs\/|(?:[a-z0-9-]+\.)*ipfs\.valve\.city/i

/** True when `host` is an IPFS endpoint that Gibs hosts. */
export const isGibsHostedIpfsHost = (host: string): boolean =>
  GIBS_HOSTED_IPFS_HOST_PATTERNS.some((pattern) => pattern.test(host))

/** True when a gateway of the signed list is neutral and serves releases. */
const isNeutralGateway = (gateway: GatewayBase): boolean =>
  !REFUSING_GATEWAYS.includes(gateway) && !isGibsHostedIpfsHost(gateway)

/**
 * The neutral gateways the page may show, in signed-list order. The result
 * falls back to the default release gateways when the signed list names none.
 */
export const listNeutralGateways = (manifest: VersionManifest): readonly GatewayBase[] => {
  const listed = manifest.gateways.filter(isNeutralGateway)
  return listed.length > 0 ? listed : DEFAULT_RELEASE_GATEWAYS.filter(isNeutralGateway)
}

/**
 * The root URL of one release on the first neutral gateway, in the subdomain
 * form `https://<cid>.ipfs.<gateway>/`, with one closing slash.
 *
 * @throws Error when no neutral gateway exists (the defaults rule this out)
 */
export const buildNeutralReleaseUrl = (manifest: VersionManifest, cid: string): string => {
  const [gateway] = listNeutralGateways(manifest)
  if (gateway === undefined) {
    throw new Error('No neutral IPFS gateway is available.')
  }
  return buildGatewayLink(gateway, cid)
}
